import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Container,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { colors, spacing, borderRadius, componentStyles } from '../styles/designSystem';

const useStyles = makeStyles((theme) => ({
  container: {
    ...componentStyles.sectionContainer,
    background: colors.gradients.hero,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(230, 0, 122, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
  },
  
  contentContainer: {
    ...componentStyles.contentContainer,
    position: 'relative',
    zIndex: 2,
  },
  
  card: {
    ...componentStyles.glassCard,
    height: '100%',
    padding: spacing.xl,
    marginBottom: spacing.lg,
  },
  
  button: {
    ...componentStyles.button.primary,
    margin: spacing.sm,
  },
  
  secondaryButton: {
    ...componentStyles.button.secondary,
    margin: spacing.sm,
  },
  tab: {
    color: colors.text.primary,
    '&.Mui-selected': {
      color: colors.primary.main,
    },
  },
  input: {
    '& .MuiOutlinedInput-root': {
      color: colors.text.primary,
      '& fieldset': {
        borderColor: colors.primary.main,
      },
      '&:hover fieldset': {
        borderColor: colors.secondary.main,
      },
    },
  },
  priceInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.lg,
    backgroundColor: colors.background.tertiary,
    borderRadius: borderRadius.base,
    margin: `${spacing.sm} 0`,
  },
}));

const DEXIntegration = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [loading] = useState(false);
  const [error] = useState('');
  const [success] = useState('');
  const [swapAmount, setSwapAmount] = useState('');
  const [tokenPrice, setTokenPrice] = useState(0);
  const [liquidityInfo, setLiquidityInfo] = useState({
    totalLiquidity: 0,
    llamaLiquidity: 0,
    solLiquidity: 0,
    volume24h: 0,
  });

  // DEX addresses
  const RAYDIUM_SWAP_URL = 'https://raydium.io/swap/?inputMint=sol&outputMint=5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb';
  const ORCA_SWAP_URL = 'https://www.orca.so/swap';
  const JUPITER_SWAP_URL = 'https://jup.ag/swap/SOL-LLAMA';

  useEffect(() => {
    fetchTokenData();
  }, []);

  // TODO: Replace with actual DEX API integration when contracts are deployed
  // This is placeholder code for development purposes
  const fetchTokenData = async () => {
    try {
      // TODO: Replace with actual DEX API calls when contracts are deployed
      // For now, using mock data to demonstrate UI functionality
      console.log('Fetching token data... (placeholder)');
      
      // Mock data for development
      setTokenPrice(0.000001); // Example price
      setLiquidityInfo({
        totalLiquidity: 50000, // SOL
        llamaLiquidity: 50000000000, // LLAMA tokens
        solLiquidity: 50, // SOL
        volume24h: 25000, // SOL
      });
      
      // TODO: Uncomment when DEX APIs are integrated
      // const response = await fetch('https://api.dexscreener.com/latest/dex/tokens/5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb');
      // const data = await response.json();
      // setTokenPrice(data.priceUsd);
      // setLiquidityInfo(data.liquidity);
    } catch (error) {
      console.error('Error fetching token data:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const openDEX = (url) => {
    window.open(url, '_blank');
  };

  const calculateSwapAmount = (inputAmount) => {
    if (!inputAmount || inputAmount <= 0) return 0;
    return (inputAmount * tokenPrice).toFixed(6);
  };



  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index} style={{ padding: '20px 0' }}>
      {value === index && children}
    </div>
  );

  return (
    <Box className={classes.container}>
      <Container className={classes.contentContainer}>
        <Typography variant="h4" gutterBottom color="primary">
          Trade LLAMA Tokens
        </Typography>
      
      <Typography variant="body1" paragraph>
        Trade LLAMA tokens securely on decentralized exchanges. Choose your preferred DEX below.
      </Typography>

      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Market Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className={classes.priceInfo}>
                <Typography variant="body1">Current Price:</Typography>
                <Typography variant="h6" color="primary">
                  ${tokenPrice.toFixed(6)} USD
                </Typography>
              </Box>
              
              <Box className={classes.priceInfo}>
                <Typography variant="body1">24h Volume:</Typography>
                <Typography variant="h6" color="primary">
                  {liquidityInfo.volume24h.toLocaleString()} SOL
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box className={classes.priceInfo}>
                <Typography variant="body1">Total Liquidity:</Typography>
                <Typography variant="h6" color="primary">
                  {liquidityInfo.totalLiquidity.toLocaleString()} SOL
                </Typography>
              </Box>
              
              <Box className={classes.priceInfo}>
                <Typography variant="body1">LLAMA in Pool:</Typography>
                <Typography variant="h6" color="primary">
                  {liquidityInfo.llamaLiquidity.toLocaleString()} LLAMA
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Swap Calculator
          </Typography>
          
          <TextField
            fullWidth
            label="Amount of SOL to swap"
            type="number"
            value={swapAmount}
            onChange={(e) => setSwapAmount(e.target.value)}
            className={classes.input}
            margin="normal"
          />
          
          {swapAmount > 0 && (
            <Box className={classes.priceInfo}>
              <Typography variant="body1">You will receive:</Typography>
              <Typography variant="h6" color="primary">
                {calculateSwapAmount(swapAmount)} LLAMA
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Choose Your DEX
          </Typography>
          
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            className={classes.tab}
          >
            <Tab 
              label="Raydium" 
              className={classes.tab}
              icon={<SwapHorizIcon />}
            />
            <Tab 
              label="Jupiter" 
              className={classes.tab}
              icon={<TrendingUpIcon />}
            />
            <Tab 
              label="Orca" 
              className={classes.tab}
              icon={<AccountBalanceWalletIcon />}
            />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Typography variant="body1" paragraph>
              Raydium is a leading Solana DEX with deep liquidity and competitive fees.
            </Typography>
            <Button
              className={classes.button}
              onClick={() => openDEX(RAYDIUM_SWAP_URL)}
              fullWidth
              variant="contained"
              startIcon={<SwapHorizIcon />}
            >
              Trade on Raydium
            </Button>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Typography variant="body1" paragraph>
              Jupiter aggregates multiple DEXs to find the best prices and lowest fees.
            </Typography>
            <Button
              className={classes.button}
              onClick={() => openDEX(JUPITER_SWAP_URL)}
              fullWidth
              variant="contained"
              startIcon={<TrendingUpIcon />}
            >
              Trade on Jupiter
            </Button>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Typography variant="body1" paragraph>
              Orca provides a user-friendly interface with concentrated liquidity pools.
            </Typography>
            <Button
              className={classes.button}
              onClick={() => openDEX(ORCA_SWAP_URL)}
              fullWidth
              variant="contained"
              startIcon={<AccountBalanceWalletIcon />}
            >
              Trade on Orca
            </Button>
          </TabPanel>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Security & Transparency
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" paragraph>
                ✅ Liquidity locked for 1 year
              </Typography>
              <Typography variant="body2" paragraph>
                ✅ Contract verified on Solana Explorer
              </Typography>
              <Typography variant="body2" paragraph>
                ✅ No team wallet access to liquidity
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="body2" paragraph>
                ✅ All transactions are public
              </Typography>
              <Typography variant="body2" paragraph>
                ✅ Price discovery through market forces
              </Typography>
              <Typography variant="body2" paragraph>
                ✅ No artificial price manipulation
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <Box display="flex" justifyContent="center" margin={2}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" className={classes.alert}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" className={classes.alert}>
          {success}
        </Alert>
      )}
      </Container>
    </Box>
  );
};

export default DEXIntegration; 